import { Injectable } from '@angular/core';
import { BaseProvider } from './base';
import { JobEventData, JobProvider, JobResponse, JobState, JobType, JobsEventData, TypeToJobMap } from './job';
import { AuthProvider } from './auth';
import { API } from './api';
import { Subscription } from 'rxjs';
import { EventName, Events } from '../utils/events';

const TRACKABLE_JOB_TYPES = [
    JobType.IncrementalTrain,
    JobType.Retrain,
    JobType.Optimize,
    JobType.Freeze,
    JobType.Train,
    JobType.AnomalyHiFi,
    JobType.BrainExport
];

const JOBS_POLLING_INTERVAL_FREQUENT_MS = 5000;
const JOBS_POLLING_INTERVAL_INFREQUENT_MS = 30000;
export const STATE_DESCRIPTION_RECALCULATE = 'Recalculating Accuracy';

let pollJobsTimeout: any;

@Injectable()
export class TrackableJobProvider extends BaseProvider {
    private isLoggedIn: boolean;
    private isLoggedInSubscription: Subscription;

    constructor(
        public api: API,
        public jobProvider: JobProvider,
        public events: Events,
        private authProvider: AuthProvider
    ) {
        super();

        this.isLoggedInSubscription = this.authProvider.isAuthenticatedSubscription.subscribe(isLoggedIn => {
            this.isLoggedIn = isLoggedIn;

            if (this.isLoggedIn) {
                this.restartJobPolling();
            } else {
                this.stopPollJobs();
            }
        });
    }

    /**
     * Start the event loop for periodically loading data about
     * all the current user or organization jobs
     */
    public async initializeJobPolling(): Promise<void> {
        if (pollJobsTimeout) {
            return;
        }

        await this.pollJobs();
    }

    /**
     * Stop and start the event loop for periodically loading data about
     * all the current user or organization jobs. This is used to ensure the
     * latest job states are loaded right after a new job is kicked off. It
     * will also force more frequent polling when running jobs are detected
     */
    public async restartJobPolling(): Promise<void> {
        this.stopPollJobs();
        await this.pollJobs();
    }

    /**
     * Start an event loop to periodically load the state of all user boost jobs
     */
    private async pollJobs(): Promise<void> {
        // Don't start polling if we're not logged in.
        // Successfully logging in will cause a restartJobPolling() call.
        if (!this.isLoggedIn) {
            return;
        }

        const updatedJobs = await this.jobProvider.loadJobs(TRACKABLE_JOB_TYPES);

        await this.loadStatesForPolledJobs(updatedJobs);

        const pollInterval = this.getJobPollingIntervalFromJobStates(updatedJobs);

        pollJobsTimeout = setTimeout(async () => {
            try {
                await this.pollJobs();
            } catch (e) {
                console.error('Job poll failed');
            }
        }, pollInterval) as any;
    }

    /**
     * Stop the event loop for periodically loading the state of all user boost jobs
     */
    public stopPollJobs(): void {
        if (pollJobsTimeout) {
            clearTimeout(pollJobsTimeout);
        }

        pollJobsTimeout = undefined;
    }

    private getJobPollingIntervalFromJobStates(jobs: JobResponse[]): number {
        for (const job of jobs) {
            if (!this.jobProvider.isJobDone(job.state) || job.state != job.previousState) {
                // At least one job running or recently changed state; poll frequently
                return JOBS_POLLING_INTERVAL_FREQUENT_MS;
            }
        }

        // Jobs are dormant; poll infrequently
        return JOBS_POLLING_INTERVAL_INFREQUENT_MS;
    }

    /**
     * Publish events associated with the latest polled job data
     */
    private async loadStatesForPolledJobs(updatedJobs: JobResponse[]) {
        const jobsEventData: JobsEventData = {
            jobs: updatedJobs
        };

        this.events.publish(EventName.TrackableJobsPolled, jobsEventData);

        for (const job of updatedJobs) {
            if (!this.jobProvider.isJobDone(job.state) || job.state != job.previousState) {
                await this.loadStateForJob(job);
            }
        }
    }

    /**
     * Load boost state for each valid job type in the dataset
     */
    public async loadJobStates(datasetIdentifier: string): Promise<TypeToJobMap> {
        const typeToJobMap = await this.jobProvider.loadDatasetJobs(datasetIdentifier, TRACKABLE_JOB_TYPES);

        for (const jobType in typeToJobMap) {
            this.loadStateForJob(typeToJobMap[jobType]);
        }

        return typeToJobMap;
    }

    /**
     * Publish events associated with the state of the specified job
     *
     * @param job The job being processed
     */
    private loadStateForJob(job: JobResponse): void {
        const jobEventData = this.jobProvider.getJobEventData(job);

        this.events.publish(EventName.TrackableJobUpdated, jobEventData);

        switch (job.state) {
            case JobState.READY:
            case JobState.WAITING:
                this.events.publish(EventName.TrackableJobWaiting, jobEventData);
                break;

            case JobState.PROCESSING:
                if (job.stateDescription == STATE_DESCRIPTION_RECALCULATE) {
                    this.events.publish(EventName.BoostRecalculating, jobEventData);
                } else {
                    this.events.publish(EventName.TrackableJobProcessing, jobEventData);
                }

                break;

            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                this.events.publish(EventName.TrackableJobCanceling, jobEventData);
                break;

            case JobState.ERROR:
                // This used to only fire if a job you triggered during the session errored out.
                // This has been disabled so we can persist the 'fail' state until it's explicitly dismissed in the job pages.
                this.events.publish(EventName.TrackableJobFailed, jobEventData);
                break;

            case JobState.DONE:
                this.events.publish(EventName.TrackableJobFinished, jobEventData);
                break;

            case JobState.CANCELED:
                this.events.publish(EventName.TrackableJobCanceled, jobEventData);
                break;
        }
    }

    public notifyJobRequestFailed(datasetIdentifier: string, jobType: JobType) {
        const jobEventData: JobEventData = {
            jobIdentifier: null,
            type: jobType,
            datasetIdentifier,
            state: JobState.ERROR
        };

        this.events.publish(EventName.TrackableJobFailed, jobEventData);
    }

    public isRelevantJob(
        datasetIdentifierFromComponent: string | undefined,
        datasetIdentifierFromJob: string | undefined,
        jobTypeFromComponent: JobType,
        jobTypeFromJob: JobType
    ): boolean {
        if (datasetIdentifierFromComponent == undefined || datasetIdentifierFromJob == undefined) {
            return false;
        }

        return datasetIdentifierFromComponent === datasetIdentifierFromJob && jobTypeFromComponent === jobTypeFromJob;
    }

    public isProgressIndeterminate(jobType: JobType): boolean {
        if (jobType == JobType.BrainExport) {
            return true;
        }

        return false;
    }

    public async dismiss(jobIdentifier: string) {
        await this.jobProvider.dismiss(jobIdentifier);
    }

    public isBrainBusy(datasetIdentifier: string): boolean {
        return this.jobProvider.isJobRunningForDataset(datasetIdentifier, TRACKABLE_JOB_TYPES);
    }
}



// WEBPACK FOOTER //
// ./src/providers/trackable-job.ts